// Phase 3: shared number/date formatters used by every report template. Matches
// the example PDF's display rules (Swiss-style thousands separator '\'', 2
// decimals, "-" for null/0). Keeping these in one place avoids per-template drift.
//
// M1.5d client feedback: zero values across the Répartition Report should
// render as "-" instead of "0.00" everywhere. That is now the DEFAULT
// behaviour of fmtNum + fmtPct. Pass { keepZero: true } when a caller
// genuinely needs to display a literal "0.00" (no current callsite needs it).

export const fmtNum = (v, { decimals = 2, keepZero = false } = {}) => {
  if (v === null || v === undefined) return "-";
  const n = Number(v);
  if (!Number.isFinite(n)) return "-";
  if (!keepZero && n === 0) return "-";
  return n.toLocaleString("de-CH", { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
};

// fmtPct kept as a thin alias of fmtNum so call-sites stay semantically
// labelled ("this column is a percentage") even though the formatting rules
// happen to be identical today. If percentage display ever needs the literal
// "%" suffix or different rounding, change just this one function.
export const fmtPct = (v, opts) => fmtNum(v, opts);

export const fmtDate = (v) => {
  if (!v) return "-";
  try {
    const d = new Date(v);
    if (Number.isNaN(d.getTime())) return "-";
    const pad = (n) => String(n).padStart(2, "0");
    return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()}`;
  } catch {
    return "-";
  }
};

// Period header label like "01.01.2024 - 31.12.2024"
export const fmtPeriod = (startISO, endISO) => `${fmtDate(startISO)} - ${fmtDate(endISO)}`;

// French long date used on the cover page ("Date du rapport : 10 juin 2026").
// No argument → today (the report-generation date), since the backend has no
// stored report date field.
export const fmtLongDateFr = (v) => {
  const d = v ? new Date(v) : new Date();
  if (Number.isNaN(d.getTime())) return "";
  const months = [
    "janvier", "février", "mars", "avril", "mai", "juin",
    "juillet", "août", "septembre", "octobre", "novembre", "décembre",
  ];
  return `${d.getDate()} ${months[d.getMonth()]} ${d.getFullYear()}`;
};

// Year label used in section/column headers and chart titles/legends.
// Single calendar year (e.g. Jan–Dec 2024)  → "2024".
// Cross-year period (e.g. 01.07.2021–30.06.2022) → short two-digit range "21/22".
export const fmtYearLabel = (startISO, endISO) => {
  const sy = startISO ? new Date(startISO).getFullYear() : null;
  const ey = endISO ? new Date(endISO).getFullYear() : null;
  if (sy == null || Number.isNaN(sy)) return "";
  if (ey == null || Number.isNaN(ey) || ey === sy) return String(sy);
  return `${String(sy).slice(-2)}/${String(ey).slice(-2)}`; // "21/22"
};

// Same value but the slash is replaced with "-" so it's safe in a filename
// (→ "Repartition_Plaines_6_21-22.pdf").
export const fmtYearFile = (startISO, endISO) => fmtYearLabel(startISO, endISO).replace("/", "-");

// Labels for the Résultat détaillé (objet) entries — PDF bookmarks and Excel tabs.
// An objet code is NOT unique per building: Confédération Centre imports lease
// 06862.20.2100.07 on module 90110 (5 m²) AND on 90710 (584.5 m²), and since
// 2026-11-04 each part gets its own page. A bare code would then name two
// different pages identically ("06862.20.2100.07" twice in the outline, or a
// meaningless "-2" tab), so ONLY a lease that repeats gets its module appended:
// "06862.20.2100.07 (90710)". Everything else keeps its bare label, unchanged.
//
// "Repeats" means the same (objet_code, reference) PAIR, never the displayed code
// alone: code-less buildings (Étang B style) display only the reference's last token
// ("010.1"), which is shared by DIFFERENT leases ("720011.503 010.1",
// "720011.504 010.1"). Their Référence differs, so they are not suffixed and keep
// exactly the labels they had before.
// `details` is the full list the entries come from; returns one label per entry.
export const objetDetailLabels = (details) => {
  const list = details || [];
  const base = list.map((d) => String(d?.objet_code || d?.reference || "Objet"));
  const identity = list.map((d) => `${d?.objet_code ?? ""}\u0001${d?.reference ?? ""}`);
  const counts = new Map();
  for (const k of identity) counts.set(k, (counts.get(k) || 0) + 1);
  return list.map((d, i) => {
    if (counts.get(identity[i]) < 2) return base[i];
    const moduleCode = String(d?.module || "").split(" - ")[0].trim();
    return moduleCode ? `${base[i]} (${moduleCode})` : base[i];
  });
};
